import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../hooks/useApi';
import Button from '../components/ui/Button';
import { Input, Select, Label } from '../components/ui/Input';
import DataGrid from '../components/grid/DataGrid';
import BulkEditBar from '../components/grid/BulkEditBar';
import BulkPreviewModal from '../components/grid/BulkPreviewModal';

// Ranging Matrix — SKU × customer, the system of record for customer-level ranging
// (migration 059; design docs/plan-ranging-matrix.md). Rows come from the D365 item
// master (vRangingMatrixItem), columns are commercial.RangingAccount, each cell is an
// effective-dated interval in commercial.CustomerRanging. Click a cell to toggle; the
// API closes the open interval or opens a new one. Right-click a cell for its history.
// Every write goes through the governed API (requireCommercialWrite + audit).

const STALE_DAYS = 90;
const LIFECYCLE_BADGE = { 'New launch': 'var(--info)', 'Phase out': 'var(--warning, #b7791f)', 'Slow moving': 'var(--warning, #b7791f)', Clearance: 'var(--danger)' };
// D365 lifecycles that mean the line is gone. Nina's call, 18 Sep 2026: Clearance
// counts as discontinued. 'Phase out' deliberately does NOT — she was offered both
// and chose only Clearance; a phase-out line is still selling and still needs
// managing in the matrix.
const DEAD_LIFECYCLES = new Set(['Obsolete', 'Pending NPD', 'Clearance']);

const brisbaneToday = () => new Date(Date.now() + 10 * 3600 * 1000).toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.round((new Date(b) - new Date(a)) / 86400000);

// Nina's export layout splits the size out of the name: "Infuzions Air Fries Cheese 10 x 90g"
// → description "Infuzions Air Fries Cheese", size "90g", pack 10. Best-effort; the code is the key.
function splitName(name) {
  const m = /^(.*?)\s+(?:(\d+)\s*[xX]\s*)?(\d+(?:\.\d+)?\s?(?:g|kg|G|KG|ml|L))\s*$/.exec(name ?? '');
  if (!m) return { desc: name ?? '', size: '' };
  return { desc: m[1].trim(), size: m[3].replace(/\s+/g, '') };
}

function pillStyle(bg, fg) {
  return { display: 'inline-block', fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 9, background: bg, color: fg, whiteSpace: 'nowrap', lineHeight: '14px' };
}

/* ── Column header: short name + supply pill + stale badge + ⋯ ───────────── */
function AccountHeader({ account, onMenu, today }) {
  const route = account.supplyRoute;
  const pill = route === 'DIRECT'
    ? <span style={pillStyle('var(--border)', 'var(--text-body)')}>Direct</span>
    : route === 'VIA'
      ? <span style={pillStyle('var(--info-soft)', 'var(--info)')}>via {account.supplyViaName ?? account.supplyViaCustomerCode}</span>
      : <span style={pillStyle('var(--warning-soft, #fdf3d8)', 'var(--warning, #b7791f)')}>Set supply method</span>;
  const stale = !account.reviewedAt || daysBetween(String(account.reviewedAt).slice(0, 10), today) > STALE_DAYS;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%', padding: '4px 0', lineHeight: 1.2 }} title={`${account.name}${account.supplyNote ? ' · ' + account.supplyNote : ''}${account.ownerName ? ' · owner ' + account.ownerName : ''}`}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        <span style={{ fontWeight: 700, fontSize: 12, color: 'var(--text-primary)', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.15 }}>{account.shortName ?? account.name}</span>
        {stale && <span title={account.reviewedAt ? `Last reviewed ${String(account.reviewedAt).slice(0, 10)}` : 'Never reviewed'} style={{ color: 'var(--warning, #b7791f)', fontSize: 11 }}>●</span>}
        <button type="button" onClick={(e) => { e.stopPropagation(); onMenu(account, e.currentTarget.getBoundingClientRect()); }}
          style={{ marginLeft: 'auto', border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: '0 2px' }} aria-label={`Options for ${account.shortName ?? account.name}`}>⋯</button>
      </div>
      {pill}
    </div>
  );
}

/* ── Cell renderer: tick, with pending-in / pending-out / overlap styling ──── */
function RangedCell({ value, data, colDef, context }) {
  const cell = data.__cells?.[colDef.field];
  const today = context.today;
  const pendingIn = cell && cell.rangedFrom > today;
  const pendingOut = cell && cell.rangedTo && cell.rangedTo > today;
  const overlap = context.lens && data.__overlap?.has(colDef.field);
  const cov = context.coverage?.[`${colDef.field}|${data.itemNumber}`];
  let bg = 'transparent', fg = 'var(--text-muted)', glyph = '';
  if (value && !pendingIn) { bg = overlap ? 'var(--warning-soft, #fdf3d8)' : 'var(--success-soft, #e6f4ea)'; fg = overlap ? 'var(--warning, #b7791f)' : 'var(--success, #1e7e34)'; glyph = '✓'; }
  if (pendingIn) { bg = 'var(--warning-soft, #fdf3d8)'; fg = 'var(--warning, #b7791f)'; glyph = `→ ${cell.rangedFrom.slice(5)}`; }
  if (pendingOut) { bg = 'var(--warning-soft, #fdf3d8)'; fg = 'var(--warning, #b7791f)'; glyph = `✓ ↦ ${cell.rangedTo.slice(5)}`; }
  return (
    <div style={{ position: 'relative', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, color: fg, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
      title={cell ? `${pendingIn ? 'Ranging in from' : 'On record since'} ${cell.rangedFrom}${cell.rangedTo ? `, out ${cell.rangedTo}` : ''}${cell.note ? ' · ' + cell.note : ''}${cell.updatedBy ? ' · ' + cell.updatedBy : ''}` : 'Not ranged — click to range'}>
      {glyph}
      {cov && (
        <div title={`${cov.ranged.toLocaleString()} / ${cov.total.toLocaleString()} stores ranged`} style={{ position: 'absolute', left: 4, right: 4, bottom: 2, height: 3, background: 'var(--border)', borderRadius: 2 }}>
          <div style={{ width: `${Math.min(100, Math.round((cov.ranged / Math.max(1, cov.total)) * 100))}%`, height: '100%', background: 'var(--info)', borderRadius: 2 }} />
        </div>
      )}
    </div>
  );
}

/* ── Small popover shell ──────────────────────────────────────────────────── */
function Popover({ anchor, onClose, width = 320, children }) {
  const top = Math.min(anchor.bottom + 6, window.innerHeight - 380);
  const left = Math.min(anchor.left, window.innerWidth - width - 12);
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60 }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', top, left, width, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 8px 24px rgba(0,0,0,0.18)', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 13 }}>
        {children}
      </div>
    </div>
  );
}

/* ── Confirm removing a ranging (Nina, 17 Sep 2026) ───────────────────────────
   A stray click used to de-range immediately. Toggling back does NOT restore the
   record: applyToggle closes the open interval at today and a re-click opens a
   NEW one starting today, so the true "on record since" date is lost. Adding a
   ranging stays one click — only removal asks. */
function ConfirmRemove({ productName, accountName, busy, onConfirm, onCancel }) {
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={busy ? undefined : onCancel}>
      <div role="alertdialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
        style={{ width: 420, maxWidth: 'calc(100vw - 32px)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', boxShadow: '0 12px 32px rgba(0,0,0,0.24)', padding: 18, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--text-primary)' }}>Remove ranging?</div>
        <div style={{ color: 'var(--text-body)', lineHeight: 1.45 }}>
          You are about to remove ranging for <b>{productName}</b> in <b>{accountName}</b>. Do you want to proceed?
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" variant="ghost" onClick={onCancel} disabled={busy}>No</Button>
          <Button size="sm" variant="primary" onClick={onConfirm} disabled={busy}>{busy ? 'Removing…' : 'Yes, remove'}</Button>
        </div>
      </div>
    </div>
  );
}

export default function RangingMatrix() {
  const apiFetch = useApi();
  const qc = useQueryClient();
  const today = brisbaneToday();

  const [asOf, setAsOf] = useState('');
  const [brand, setBrand] = useState('');
  const [lifecycle, setLifecycle] = useState('');
  const [channel, setChannel] = useState('');
  const [search, setSearch] = useState('');
  const [rangedOnly, setRangedOnly] = useState(false);
  const [lens, setLens] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [showDiscontinued, setShowDiscontinued] = useState(false);
  const [showJobLots, setShowJobLots] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(null); // {customerCode, itemNumber, productName, accountName, busy}
  const [gridApi, setGridApi] = useState(null);
  const [selected, setSelected] = useState(0);
  const [bulk, setBulk] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [menu, setMenu] = useState(null);       // {account, anchor}
  const [history, setHistory] = useState(null); // {customerCode, itemNumber, anchor, data}
  const [addOpen, setAddOpen] = useState(false);
  const [importState, setImportState] = useState(null);
  const fileRef = useRef(null);

  const matrixKey = ['customer-ranging-matrix', asOf || 'current'];
  const { data, isLoading, error: loadError } = useQuery({
    queryKey: matrixKey,
    queryFn: () => apiFetch(`/customer-ranging/matrix${asOf ? `?asOf=${asOf}` : ''}`).then((r) => { if (!r.ok) throw new Error(`API ${r.status}`); return r.json(); }),
    staleTime: 60_000,
  });
  const refresh = useCallback(() => qc.invalidateQueries({ queryKey: ['customer-ranging-matrix'] }), [qc]);

  // visibleAccounts = every column the user could see; accounts = what the channel
  // filter actually shows. The overlap lens is computed from visibleAccounts on
  // purpose: filtering to "P&C" must not drop the Metcash / TDG columns that the
  // lens compares against, or the lens would silently read empty.
  const visibleAccounts = useMemo(() => (data?.accounts ?? []).filter((a) => showHidden || a.isVisible), [data, showHidden]);
  const accounts = useMemo(() => (channel ? visibleAccounts.filter((a) => (a.channel ?? '') === channel) : visibleAccounts), [visibleAccounts, channel]);
  const accountByCode = useMemo(() => Object.fromEntries((data?.accounts ?? []).map((a) => [a.customerCode, a])), [data]);
  const cellMap = useMemo(() => {
    const m = {};
    for (const c of data?.cells ?? []) m[`${c.customerCode}|${c.itemNumber}`] = c;
    return m;
  }, [data]);
  const coverage = useMemo(() => {
    const m = {};
    for (const c of data?.storeCoverage ?? []) m[`${c.customerCode}|${c.itemNumber}`] = c;
    return m;
  }, [data]);
  const brands = useMemo(() => [...new Set((data?.items ?? []).map((i) => i.brand).filter(Boolean))].sort(), [data]);
  const lifecycles = useMemo(() => [...new Set((data?.items ?? []).map((i) => i.lifecycle).filter(Boolean))].sort(), [data]);
  const channels = useMemo(() => [...new Set(visibleAccounts.map((a) => a.channel).filter(Boolean))].sort(), [visibleAccounts]);
  // Who counts as a wholesaler / distributor is derived from the SUPPLY ROUTE — an account
  // others are supplied "via" — not from the Channel label. Channel is the CRM segment:
  // it is free text the team edits in the app, so keying the direct-overlap lens off it
  // meant re-filing Metcash from "Wholesale" to "Independent Grocery" would silently move
  // Metcash onto the "invoiced direct, could go non-direct" list (Nina, 18 Sep 2026).
  // Built from ALL accounts, not just visible ones, so hiding a downstream column cannot
  // demote its wholesaler.
  const suppliedVia = useMemo(
    () => new Set((data?.accounts ?? []).filter((a) => a.supplyRoute === 'VIA' && a.supplyViaCustomerCode).map((a) => a.supplyViaCustomerCode)),
    [data],
  );

  // Row = item + one boolean per account (ranged now, or pending) + per-row helper maps.
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const wholesalers = visibleAccounts.filter((a) => suppliedVia.has(a.customerCode)).map((a) => a.customerCode);
    const directs = visibleAccounts.filter((a) => a.supplyRoute === 'DIRECT' && !suppliedVia.has(a.customerCode)).map((a) => a.customerCode);
    return (data?.items ?? []).filter((it) => {
      if (brand && it.brand !== brand) return false;
      if (lifecycle && (it.lifecycle ?? '') !== lifecycle) return false;
      if (q && !`${it.itemNumber} ${it.productName ?? ''} ${it.ean ?? ''}`.toLowerCase().includes(q)) return false;
      return true;
    }).map((it) => {
      const r = { ...it, __cells: {}, __overlap: new Set() };
      let any = false;
      // Cells are built over visibleAccounts so __any / __dead stay true to the whole
      // record — a SKU still ranged in a column the channel filter hides is not dead.
      for (const a of visibleAccounts) {
        const c = cellMap[`${a.customerCode}|${it.itemNumber}`];
        r.__cells[a.customerCode] = c ?? null;
        r[a.customerCode] = !!c;
        if (c) any = true;
      }
      r.__any = any;
      // Discontinued = D365 says the line is gone AND nothing still ranges it. Such a
      // row only survives vRangingMatrixItem because of its historical ranging rows
      // (059's EXISTS clause), so hide it here rather than dropping it server-side —
      // dropping it would orphan an open interval with no way to see or close it.
      r.__dead = DEAD_LIFECYCLES.has(it.lifecycle ?? '') && !any;
      const wholesaleRanged = wholesalers.some((w) => r[w] && r.__cells[w].rangedFrom <= today);
      if (wholesaleRanged) for (const d of directs) if (r[d]) r.__overlap.add(d);
      return r;
    }).filter((r) => (!rangedOnly || r.__any) && (showDiscontinued || !r.__dead) && (showJobLots || !r.isJobLot));
  }, [data, visibleAccounts, suppliedVia, cellMap, brand, lifecycle, search, rangedOnly, showDiscontinued, showJobLots, today]);

  // Count what the toggle is hiding, so "0 discontinued" reads differently from "hidden".
  const deadCount = useMemo(() => {
    if (!data?.items) return 0;
    return data.items.filter((it) => DEAD_LIFECYCLES.has(it.lifecycle ?? '')
      && !visibleAccounts.some((a) => cellMap[`${a.customerCode}|${it.itemNumber}`])).length;
  }, [data, visibleAccounts, cellMap]);

  const jobLotCount = useMemo(() => (data?.items ?? []).filter((i) => i.isJobLot).length, [data]);

  // Only count overlaps on columns the user can actually see right now.
  const overlapCount = useMemo(() => {
    const shown = new Set(accounts.map((a) => a.customerCode));
    return rows.reduce((n, r) => n + [...r.__overlap].filter((c) => shown.has(c)).length, 0);
  }, [rows, accounts]);

  const columns = useMemo(() => [
    { headerName: 'Code', field: 'itemNumber', pinned: 'left', width: 82, cellStyle: { fontFamily: 'ui-monospace, monospace', fontSize: 12 } },
    { headerName: 'Product', field: 'productName', pinned: 'left', width: 300, tooltipField: 'productName',
      cellRenderer: (p) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, overflow: 'hidden' }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.value}</span>
          {LIFECYCLE_BADGE[p.data.lifecycle] && <span style={pillStyle('transparent', LIFECYCLE_BADGE[p.data.lifecycle])}>{p.data.lifecycle}</span>}
          {p.data.isJobLot ? <span title="Job lot — one-off opportunistic volume, not part of the standing range" style={pillStyle('var(--info-soft)', 'var(--info)')}>Job lot</span> : null}
          {p.data.isPinned ? <span title="Pinned into the matrix" style={{ fontSize: 10, color: 'var(--text-muted)' }}>📌</span> : null}
        </span>
      ) },
    { headerName: 'Ctn', field: 'packQty', pinned: 'left', width: 58, valueFormatter: (p) => (p.value == null ? '' : String(Number(p.value))), filter: false },
    ...accounts.map((a) => ({
      headerName: a.shortName ?? a.name, field: a.customerCode, width: 132, minWidth: 96, sortable: false, filter: false, resizable: true,
      headerComponent: AccountHeader, headerComponentParams: { account: a, today, onMenu: (acct, anchor) => setMenu({ account: acct, anchor }) },
      cellRenderer: RangedCell, cellStyle: { padding: 0 },
      cellClass: a.isVisible ? undefined : 'ranging-hidden-col',
    })),
  ], [accounts, today]);

  /* ── toggle one cell (click) ───────────────────────────────────────────── */
  const toggle = useCallback(async (customerCode, itemNumber, ranged, effectiveFrom, note) => {
    const res = await apiFetch('/customer-ranging', { method: 'PUT', body: JSON.stringify({ customerCode, itemNumber, ranged, effectiveFrom: effectiveFrom || undefined, note: note || undefined }) });
    if (!res.ok) {
      const j = await res.json().catch(() => ({}));
      throw new Error(res.status === 403 ? 'You have read-only (Viewer) access — editing requires the Editor role.' : (j?.error ?? `Toggle failed (${res.status})`));
    }
    return res.json();
  }, [apiFetch]);

  const onCellClicked = useCallback(async (e) => {
    if (asOf) return;                                    // historical view is read-only
    const code = e.colDef.field;
    const acct = accountByCode[code];
    if (!acct) return;                                   // pinned item columns
    const current = !!e.data[code];
    // Removing is destructive and not cleanly undoable — ask. Adding stays one click.
    if (current) {
      setConfirmRemove({ customerCode: code, itemNumber: e.data.itemNumber, productName: e.data.productName, accountName: acct.shortName ?? acct.name, busy: false });
      return;
    }
    try {
      setError(null);
      await toggle(code, e.data.itemNumber, true);
      refresh();
    } catch (err) { setError(`${e.data.productName}: ${String(err.message ?? err)}`); }
  }, [asOf, accountByCode, toggle, refresh]);

  // Right-click → "Ranged / Not ranged from date". Always reports what the server actually
  // did: a request that changes nothing comes back as no-op, and silently closing the
  // popover on a no-op is what made a mis-clicked start date look uncorrectable.
  const applyEffective = useCallback(async (ranged) => {
    if (!history) return;
    const label = `${history.productName} · ${accountByCode[history.customerCode]?.shortName ?? history.customerCode}`;
    try {
      setError(null); setNotice(null);
      const r = await toggle(history.customerCode, history.itemNumber, ranged, history.effective, history.note);
      if (r?.action === 'no-op') {
        setNotice(`${label}: already ${ranged ? 'ranged' : 'not ranged'} from ${history.effective} — nothing to change.`);
      } else if (r?.action === 'merged') {
        setNotice(`${label}: start date corrected to ${history.effective}; the earlier intervals were joined into one.`);
      } else if (r?.action === 'amended') {
        setNotice(`${label}: start date changed to ${history.effective}.`);
      } else {
        setNotice(`${label}: ${ranged ? 'ranged' : 'de-ranged'} from ${history.effective}.`);
      }
      setHistory(null);
      refresh();
    } catch (err) { setError(`${label}: ${String(err.message ?? err)}`); }
  }, [history, accountByCode, toggle, refresh]);

  const commitRemove = useCallback(async () => {
    if (!confirmRemove) return;
    setConfirmRemove((c) => ({ ...c, busy: true }));
    try {
      setError(null);
      await toggle(confirmRemove.customerCode, confirmRemove.itemNumber, false);
      setConfirmRemove(null);
      refresh();
    } catch (err) {
      setError(`${confirmRemove.productName}: ${String(err.message ?? err)}`);
      setConfirmRemove(null);
    }
  }, [confirmRemove, toggle, refresh]);

  const onCellContextMenu = useCallback(async (e) => {
    const code = e.colDef.field;
    if (!accountByCode[code]) return;
    e.event.preventDefault();
    const anchor = { left: e.event.clientX, bottom: e.event.clientY };
    setHistory({ customerCode: code, itemNumber: e.data.itemNumber, productName: e.data.productName, anchor, data: null, effective: today });
    const res = await apiFetch(`/customer-ranging/history?customerCode=${encodeURIComponent(code)}&itemNumber=${encodeURIComponent(e.data.itemNumber)}`);
    const j = await res.json().catch(() => null);
    setHistory((h) => (h && h.customerCode === code && h.itemNumber === e.data.itemNumber ? { ...h, data: j } : h));
  }, [accountByCode, apiFetch, today]);

  /* ── bulk: select rows → set account = ranged / not → governed import ──── */
  const bulkFields = useMemo(() => accounts.map((a) => ({ key: a.customerCode, label: a.shortName ?? a.name })), [accounts]);
  async function onBulkApply(customerCode, value) {
    const sel = gridApi?.getSelectedRows() ?? [];
    if (!sel.length) return;
    const ranged = value === '1';
    const rowsToSend = sel.map((r) => ({ customerCode, itemNumber: r.itemNumber, ranged: ranged ? 1 : 0 }));
    const label = accountByCode[customerCode]?.shortName ?? customerCode;
    setBulk({ rows: rowsToSend, label, ranged, applying: false });
    try {
      const res = await apiFetch('/customer-ranging/import', { method: 'POST', body: JSON.stringify({ rows: rowsToSend, dryRun: true, fileName: `bulk:${customerCode}` }) });
      const p = await res.json();
      if (!res.ok) throw new Error(p?.error ?? `Preview failed (${res.status})`);
      setBulk((s) => ({ ...s, lines: [`${p.opens} SKU(s) to range, ${p.closes} to de-range at ${label}.`, `${sel.length} row(s) selected → ${ranged ? 'Ranged' : 'Not ranged'} from ${p.effective}.`] }));
    } catch (err) { setBulk((s) => ({ ...s, error: String(err.message ?? err) })); }
  }
  async function commitBulk() {
    setBulk((s) => ({ ...s, applying: true }));
    try {
      const res = await apiFetch('/customer-ranging/import', { method: 'POST', body: JSON.stringify({ rows: bulk.rows, dryRun: false, fileName: `bulk:${bulk.label}` }) });
      const r = await res.json();
      if (!res.ok) throw new Error(r?.error ?? `Apply failed (${res.status})`);
      setBulk((s) => ({ ...s, applying: false, done: `Done — ${r.opens} ranged, ${r.closes} de-ranged.` }));
      refresh(); gridApi?.deselectAll(); setSelected(0);
    } catch (err) { setBulk((s) => ({ ...s, applying: false, error: String(err.message ?? err) })); }
  }

  /* ── Excel round-trip in Nina's layout ─────────────────────────────────── */
  async function exportExcel() {
    const XLSX = await import('xlsx');
    const out = rows.map((r) => {
      const { desc, size } = splitName(r.productName);
      const o = { 'Item Code': r.itemNumber, 'Product Description': desc, Size: size, 'Ctn Qty': r.packQty == null ? '' : Number(r.packQty) };
      for (const a of accounts) o[a.shortName ?? a.name] = r[a.customerCode] ? 'X' : '';
      return o;
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(out), 'Ranging');
    XLSX.writeFile(wb, `RangingMatrix_${asOf || today}.xlsx`);
  }
  async function onImportFile(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const XLSX = await import('xlsx');
    const wb = XLSX.read(await file.arrayBuffer());
    const raw = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: '' });
    // Header → account: match short name or name, case-insensitive. Unknown headers are reported, not guessed.
    const byHeader = {};
    for (const a of data?.accounts ?? []) { byHeader[(a.shortName ?? '').toLowerCase()] = a.customerCode; byHeader[a.name.toLowerCase()] = a.customerCode; }
    const skip = new Set(['item code', 'product description', 'size', 'ctn qty']);
    const headers = raw.length ? Object.keys(raw[0]) : [];
    const unknownHeaders = headers.filter((h) => !skip.has(h.toLowerCase()) && !byHeader[h.toLowerCase()]);
    const rowsToSend = [];
    for (const r of raw) {
      const code = String(r['Item Code'] ?? '').trim();
      if (!/^\d{5,}$/.test(code)) continue;
      for (const h of headers) {
        const acct = byHeader[h.toLowerCase()];
        if (!acct) continue;
        rowsToSend.push({ customerCode: acct, itemNumber: code, ranged: String(r[h]).trim().toUpperCase() === 'X' ? 1 : 0 });
      }
    }
    if (!rowsToSend.length) { setImportState({ fileName: file.name, error: 'No usable rows — expected an "Item Code" column plus one column per account.' }); return; }
    setImportState({ fileName: file.name, rows: rowsToSend, unknownHeaders, preview: null, replace: true });
    try {
      const res = await apiFetch('/customer-ranging/import', { method: 'POST', body: JSON.stringify({ rows: rowsToSend, dryRun: true, fileName: file.name, replace: true }) });
      const p = await res.json();
      if (!res.ok) throw new Error(p?.error ?? `Preview failed (${res.status})`);
      setImportState((s) => ({ ...s, preview: p }));
    } catch (err) { setImportState((s) => ({ ...s, error: String(err.message ?? err) })); }
  }
  async function commitImport() {
    setImportState((s) => ({ ...s, committing: true }));
    try {
      const res = await apiFetch('/customer-ranging/import', { method: 'POST', body: JSON.stringify({ rows: importState.rows, dryRun: false, fileName: importState.fileName, replace: importState.replace }) });
      const r = await res.json();
      if (!res.ok) throw new Error(r?.error ?? `Import failed (${res.status})`);
      setImportState((s) => ({ ...s, committing: false, result: r }));
      refresh();
    } catch (err) { setImportState((s) => ({ ...s, committing: false, error: String(err.message ?? err) })); }
  }

  /* ── account menu actions ──────────────────────────────────────────────── */
  async function updateAccount(code, body) {
    const res = await apiFetch(`/ranging-accounts/${encodeURIComponent(code)}`, { method: 'PUT', body: JSON.stringify(body) });
    const j = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(res.status === 403 ? 'Read-only (Viewer) access.' : (j?.error ?? `Update failed (${res.status})`));
    refresh();
  }
  /* "Change customer" on a column is a customer MERGE (crmMerge.ts), not a field edit: the
   * column and every cell are re-keyed to the real account and the old code is retired
   * pointing at it. Dry-run first so the confirm names exactly what will move. */
  async function mergeAccount(from, to) {
    const call = (dryRun) => apiFetch(`/crm-customer/${encodeURIComponent(from)}/merge-into/${encodeURIComponent(to)}`, { method: 'POST', body: JSON.stringify({ dryRun }) });
    const parse = async (res) => {
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(res.status === 403 ? 'Read-only (Viewer) access.' : (j?.error ?? `Merge failed (${res.status})`));
      return j;
    };
    const { plan } = await parse(await call(true));
    const lines = plan.moves.map((m) => `• ${m.n} ${m.label}`);
    if (plan.ranging.openIntervalsDropped || plan.ranging.openIntervalsClosed) lines.push(`• ${plan.ranging.openIntervalsDropped + plan.ranging.openIntervalsClosed} cell(s) already ranged on ${plan.to.code} — the target's dates win`);
    if (plan.supplyRouteCopied) lines.push(`• supply method (${plan.supplyRouteCopied.supplyRoute}${plan.supplyRouteCopied.supplyViaCustomerCode ? ' via ' + plan.supplyRouteCopied.supplyViaCustomerCode : ''}) copied to ${plan.to.code}, which had none`);
    const okGo = window.confirm(`Move everything on ${plan.from.name} (${plan.from.code}) to ${plan.to.name} (${plan.to.code})?\n\n${lines.join('\n')}\n\n${plan.from.code} is then retired as merged into ${plan.to.code}. Effective dates are kept.`);
    if (!okGo) return;
    await parse(await call(false));
    refresh();
  }
  async function setItemMode(itemNumber, mode) {
    const res = await apiFetch(`/ranging-matrix-items/${encodeURIComponent(itemNumber)}`, { method: 'PUT', body: JSON.stringify({ mode }) });
    if (!res.ok) { const j = await res.json().catch(() => ({})); setError(j?.error ?? `Update failed (${res.status})`); return; }
    refresh();
  }

  // Job lot is independent of pin/hide, so send ONLY isJobLot — the server leaves
  // any scope override on that item alone (072).
  async function setJobLot(itemNumber, isJobLot) {
    const res = await apiFetch(`/ranging-matrix-items/${encodeURIComponent(itemNumber)}`, { method: 'PUT', body: JSON.stringify({ isJobLot }) });
    if (!res.ok) { const j = await res.json().catch(() => ({})); setError(j?.error ?? `Update failed (${res.status})`); return; }
    refresh();
  }
  async function forSelected(fn) {
    for (const r of gridApi.getSelectedRows()) await fn(r.itemNumber);
    gridApi.deselectAll(); setSelected(0);
  }

  useEffect(() => { if (notice) { const t = setTimeout(() => setNotice(null), 4000); return () => clearTimeout(t); } }, [notice]);

  // Housekeeping nudges cover the whole matrix, not the filtered view — a channel
  // filter must not make "3 accounts need a supply method" quietly disappear.
  const staleCount = visibleAccounts.filter((a) => !a.reviewedAt || daysBetween(String(a.reviewedAt).slice(0, 10), today) > STALE_DAYS).length;
  const unsetSupply = visibleAccounts.filter((a) => !a.supplyRoute).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>Ranging Matrix</h1>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
          {rows.length} SKUs × {accounts.length} accounts · {Object.keys(cellMap).length} ranged cells
          {asOf ? ` · as at ${asOf} (read-only)` : ''}
        </span>
        {(unsetSupply > 0 || staleCount > 0) && !asOf && (
          <span style={{ fontSize: 12, color: 'var(--warning, #b7791f)' }}>
            {unsetSupply > 0 && `${unsetSupply} account(s) need a supply method`}{unsetSupply > 0 && staleCount > 0 && ' · '}{staleCount > 0 && `${staleCount} column(s) not reviewed in ${STALE_DAYS} days`}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <Input placeholder="Search code / product / EAN" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 220 }} />
        <Select value={brand} onChange={(e) => setBrand(e.target.value)} style={{ width: 150 }}><option value="">All brands</option>{brands.map((b) => <option key={b}>{b}</option>)}</Select>
        <Select value={lifecycle} onChange={(e) => setLifecycle(e.target.value)} style={{ width: 160 }}><option value="">All lifecycles</option>{lifecycles.map((l) => <option key={l}>{l}</option>)}</Select>
        <Select title="Show only the customer columns in this channel" value={channel} onChange={(e) => setChannel(e.target.value)} style={{ width: 170 }}><option value="">All channels</option>{channels.map((c) => <option key={c}>{c}</option>)}</Select>
        <label style={{ fontSize: 13, display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-body)' }}><input type="checkbox" checked={rangedOnly} onChange={(e) => setRangedOnly(e.target.checked)} />Ranged anywhere</label>
        <label title="Highlight SKUs we invoice direct that an account we also supply through (set via each column's supply method) already ranges" style={{ fontSize: 13, display: 'flex', gap: 5, alignItems: 'center', color: lens ? 'var(--warning, #b7791f)' : 'var(--text-body)' }}>
          <input type="checkbox" checked={lens} onChange={(e) => setLens(e.target.checked)} />Direct-overlap lens{lens ? ` (${overlapCount})` : ''}
        </label>
        <label style={{ fontSize: 13, display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-body)' }}>As at <Input type="date" value={asOf} max={today} onChange={(e) => setAsOf(e.target.value)} style={{ width: 140 }} />{asOf && <Button size="sm" variant="ghost" onClick={() => setAsOf('')}>now</Button>}</label>
        <label style={{ fontSize: 13, display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-muted)' }}><input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />Show hidden columns</label>
        <label title="Lines D365 marks Clearance, Obsolete or Pending NPD that nothing ranges any more" style={{ fontSize: 13, display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={showDiscontinued} onChange={(e) => setShowDiscontinued(e.target.checked)} />Show discontinued{deadCount ? ` (${deadCount})` : ''}
        </label>
        <label title="Job lots are one-off opportunistic volume, not part of the standing range — to mark one, tick its row and use Mark job lot in the bar above the grid" style={{ fontSize: 13, display: 'flex', gap: 5, alignItems: 'center', color: 'var(--text-muted)' }}>
          <input type="checkbox" checked={showJobLots} onChange={(e) => setShowJobLots(e.target.checked)} />Show job lots{jobLotCount ? ` (${jobLotCount})` : ''}
        </label>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <Button size="sm" onClick={exportExcel} disabled={!rows.length}>Export</Button>
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={!!asOf}>Import</Button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={onImportFile} />
          <Button size="sm" variant="primary" onClick={() => setAddOpen(true)} disabled={!!asOf}>+ Add customer</Button>
        </div>
      </div>

      {error && <div style={{ color: 'var(--danger)', fontSize: 13 }}>{error}</div>}
      {notice && <div style={{ color: 'var(--success, #1e7e34)', fontSize: 13 }}>{notice}</div>}
      {loadError && <div style={{ color: 'var(--danger)', fontSize: 13 }}>Could not load the matrix: {String(loadError.message)}</div>}

      <BulkEditBar selectedCount={selected} fields={bulkFields} noun="SKUs"
        valueOptions={[{ value: '1', label: 'Ranged' }, { value: '0', label: 'Not ranged' }]}
        onApply={onBulkApply} onClear={() => { gridApi?.deselectAll(); setSelected(0); }} applying={!!bulk?.applying}>
        {!asOf && (
          <>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Job lot</span>
            <Button size="sm" onClick={() => forSelected((n) => setJobLot(n, true))}>Mark job lot</Button>
            <Button size="sm" onClick={() => forSelected((n) => setJobLot(n, false))}>Clear job lot</Button>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 12 }}>Row scope</span>
            <Button size="sm" onClick={() => forSelected((n) => setItemMode(n, 'hide'))}>Hide from matrix</Button>
            <Button size="sm" onClick={() => forSelected((n) => setItemMode(n, 'pin'))}>Pin</Button>
            <Button size="sm" onClick={() => forSelected((n) => setItemMode(n, null))}>Clear override</Button>
          </>
        )}
      </BulkEditBar>

      {isLoading ? <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading…</div> : (
        <DataGrid
          rowData={rows} columnDefs={columns} context={{ today, lens, coverage }}
          headerHeight={68}
          onGridReady={(e) => setGridApi(e.api)}
          onCellClicked={onCellClicked}
          onCellContextMenu={onCellContextMenu}
          onSelectionChanged={(e) => setSelected(e.api.getSelectedRows().length)}
          rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true, selectAll: 'filtered', enableClickSelection: false }}
          getRowId={(p) => String(p.data.itemNumber)}
          getContextMenuItems={() => []}
          preventDefaultOnContextMenu
        />
      )}

      <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span><b style={{ color: 'var(--success, #1e7e34)' }}>✓</b> ranged · click to toggle · right-click for history / effective date</span>
        <span><b style={{ color: 'var(--warning, #b7791f)' }}>→ 09-23</b> ranging in on that date · <b style={{ color: 'var(--warning, #b7791f)' }}>✓ ↦</b> delisting on that date</span>
        <span><b style={{ color: 'var(--info)' }}>▬</b> blue bar under a Woolworths / Coles tick = how many of their stores range it (context only, not a ranging state)</span>
        <span><b style={{ color: 'var(--warning, #b7791f)' }}>●</b> column not reviewed in {STALE_DAYS} days</span>
      </div>

      {confirmRemove && (
        <ConfirmRemove productName={confirmRemove.productName} accountName={confirmRemove.accountName} busy={confirmRemove.busy}
          onConfirm={commitRemove} onCancel={() => setConfirmRemove(null)} />
      )}

      {bulk && <BulkPreviewModal title={`Set ${bulk.label} = ${bulk.ranged ? 'Ranged' : 'Not ranged'}`} lines={bulk.lines} error={bulk.error} applying={bulk.applying} done={bulk.done} onApply={commitBulk} onClose={() => setBulk(null)} />}

      {menu && (
        <AccountMenu account={accountByCode[menu.account.customerCode] ?? menu.account} anchor={menu.anchor} allCustomers={data?.accounts ?? []} today={today}
          onClose={() => setMenu(null)} onSave={async (body) => { try { await updateAccount(menu.account.customerCode, body); setNotice('Saved.'); } catch (err) { setError(String(err.message ?? err)); } }}
          onMerge={async (to) => { try { await mergeAccount(menu.account.customerCode, to); setNotice(`Column moved to ${to}.`); } catch (err) { setError(String(err.message ?? err)); } }} apiFetch={apiFetch} />
      )}

      {history && (
        <Popover anchor={history.anchor} onClose={() => setHistory(null)} width={360}>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{history.productName}</div>
          <div style={{ color: 'var(--text-muted)' }}>{accountByCode[history.customerCode]?.shortName ?? history.customerCode} · #{history.itemNumber}</div>
          {!history.data ? <div style={{ color: 'var(--text-muted)' }}>Loading history…</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 160, overflow: 'auto' }}>
              {history.data.intervals.length === 0 && <div style={{ color: 'var(--text-muted)' }}>Never ranged.</div>}
              {history.data.intervals.map((iv) => (
                <div key={iv.rangingId} style={{ fontSize: 12 }}>
                  <b>{iv.rangedFrom}</b> → {iv.rangedTo ?? 'open'} · {iv.source}{iv.note ? ` · ${iv.note}` : ''}{iv.updatedBy ? ` · ${iv.updatedBy}` : ''}
                </div>
              ))}
            </div>
          )}
          {!asOf && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <Label>Set with an effective date</Label>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Input type="date" value={history.effective} onChange={(e) => setHistory((h) => ({ ...h, effective: e.target.value }))} style={{ width: 140 }} />
                <Input placeholder="note (optional)" value={history.note ?? ''} onChange={(e) => setHistory((h) => ({ ...h, note: e.target.value }))} style={{ flex: 1 }} />
              </div>
              <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                <Button size="sm" onClick={() => applyEffective(false)}>Not ranged from date</Button>
                <Button size="sm" variant="primary" onClick={() => applyEffective(true)}>Ranged from date</Button>
              </div>
            </div>
          )}
        </Popover>
      )}

      {addOpen && <AddCustomerModal apiFetch={apiFetch} existing={data?.accounts ?? []} onClose={() => setAddOpen(false)} onDone={(msg) => { setAddOpen(false); setNotice(msg); refresh(); }} />}

      {importState && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setImportState(null)} />
          <div style={{ position: 'relative', width: 'min(600px, 94vw)', maxHeight: '86vh', overflow: 'auto', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Import — {importState.fileName}</div>
            {importState.error && <div style={{ color: 'var(--danger)' }}>{importState.error}</div>}
            {!importState.error && !importState.preview && !importState.result && <div style={{ color: 'var(--text-muted)' }}>Analysing file against the database…</div>}
            {importState.unknownHeaders?.length > 0 && <div style={{ color: 'var(--warning, #b7791f)' }}>Columns not matched to an account (ignored): {importState.unknownHeaders.join(', ')}. Add the customer first if it should be a column.</div>}
            {!importState.error && importState.preview && !importState.result && (
              <>
                <div>{importState.preview.accountsInFile} account column(s) · <b>{importState.preview.opens}</b> to range · <b>{importState.preview.closes}</b> to de-range{importState.replace ? ' (file is authoritative for the columns it contains)' : ''}</div>
                {importState.preview.unknownItems?.length > 0 && <div style={{ color: 'var(--danger)' }}>Unknown item codes (skipped): {importState.preview.unknownItems.slice(0, 20).join(', ')}{importState.preview.unknownItems.length > 20 ? '…' : ''}</div>}
                {importState.preview.sampleOpens?.length > 0 && <div style={{ color: 'var(--text-muted)' }}>{importState.preview.sampleOpens.slice(0, 8).join(' · ')}</div>}
                {importState.preview.sampleCloses?.length > 0 && <div style={{ color: 'var(--text-muted)' }}>{importState.preview.sampleCloses.slice(0, 8).join(' · ')}</div>}
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <Button size="sm" onClick={() => setImportState(null)}>Cancel</Button>
                  <Button size="sm" variant="primary" disabled={importState.committing || (importState.preview.opens === 0 && importState.preview.closes === 0)} onClick={commitImport}>{importState.committing ? 'Importing…' : 'Apply Import'}</Button>
                </div>
              </>
            )}
            {importState.result && (
              <>
                <div>Done — {importState.result.opens} ranged, {importState.result.closes} de-ranged.</div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}><Button size="sm" variant="primary" onClick={() => setImportState(null)}>Close</Button></div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── ⋯ menu for a column ──────────────────────────────────────────────────── */
function AccountMenu({ account, anchor, allCustomers, today, onClose, onSave, onMerge, apiFetch }) {
  const [route, setRoute] = useState(account.supplyRoute ?? '');
  const [via, setVia] = useState(account.supplyViaCustomerCode ?? '');
  const [note, setNote] = useState(account.supplyNote ?? '');
  const [owner, setOwner] = useState(account.ownerName ?? '');
  const [channel, setChannel] = useState(account.channel ?? '');
  const [moveTo, setMoveTo] = useState('');
  const [viaOptions, setViaOptions] = useState(null);
  const [segments, setSegments] = useState(null);
  useEffect(() => {
    // "via" targets are any active customer, not just matrix columns (Metcash is a column, TDG may not be).
    apiFetch('/master-customers').then((r) => (r.ok ? r.json() : [])).then((list) => setViaOptions(Array.isArray(list) ? list : [])).catch(() => setViaOptions([]));
    // The channel list is commercial.CrmSegment — the same list the CRM calls "Segment" (061).
    apiFetch('/crm-segments').then((r) => (r.ok ? r.json() : [])).then((list) => setSegments(Array.isArray(list) ? list : (list?.segments ?? []))).catch(() => setSegments([]));
  }, [apiFetch]);
  const options = viaOptions ?? allCustomers;
  const reviewed = account.reviewedAt ? String(account.reviewedAt).slice(0, 10) : null;
  return (
    <Popover anchor={anchor} onClose={onClose} width={340}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{account.name}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{account.customerCode} · {account.channel ?? 'no channel'}{!account.isActive ? ' · inactive in master' : ''}</div>
      <Label>Supply method</Label>
      <div style={{ display: 'flex', gap: 6 }}>
        <Select value={route} onChange={(e) => setRoute(e.target.value)} style={{ flex: 1 }}>
          <option value="">Not set</option><option value="DIRECT">Direct — we invoice them</option><option value="VIA">Via another customer</option>
        </Select>
        {route === 'VIA' && (
          <Select value={via} onChange={(e) => setVia(e.target.value)} style={{ flex: 1 }}>
            <option value="">choose…</option>
            {options.filter((c) => c.customerCode !== account.customerCode && (c.isActive ?? true)).map((c) => <option key={c.customerCode} value={c.customerCode}>{c.shortName ?? c.name}</option>)}
          </Select>
        )}
      </div>
      <Input placeholder="note, e.g. Metcash for grocery, direct for pallets" value={note} onChange={(e) => setNote(e.target.value)} />
      <Label>Channel (the CRM calls this Segment)</Label>
      <Select value={channel} onChange={(e) => setChannel(e.target.value)} title="Drives the Channel filter above. It does NOT decide who counts as a wholesaler — that comes from the supply method.">
        <option value="">Not set</option>
        {(segments ?? []).filter((s) => s.isActive ?? true).map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        {channel && !(segments ?? []).some((s) => s.name === channel) && <option value={channel}>{channel}</option>}
      </Select>
      <Label>Owner (keeps this column honest)</Label>
      <Input placeholder="name" value={owner} onChange={(e) => setOwner(e.target.value)} />
      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{reviewed ? `Last reviewed ${reviewed}${account.reviewedBy ? ` by ${account.reviewedBy}` : ''}` : 'Never reviewed'}</div>
      <Label>Change customer</Label>
      <div style={{ display: 'flex', gap: 6 }}>
        <Select value={moveTo} onChange={(e) => setMoveTo(e.target.value)} style={{ flex: 1 }} title="Re-point this column and its cells at another master customer. The current code is retired as merged into it.">
          <option value="">{viaOptions ? 'move this column to…' : 'loading…'}</option>
          {options.filter((c) => c.customerCode !== account.customerCode && !c.retiredAt).sort((a, b) => (a.shortName ?? a.name).localeCompare(b.shortName ?? b.name)).map((c) => <option key={c.customerCode} value={c.customerCode}>{c.shortName ?? c.name} — {c.customerCode}</option>)}
        </Select>
        <Button size="sm" disabled={!moveTo} onClick={() => { onMerge(moveTo); onClose(); }}>Move…</Button>
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>For a column created against a placeholder (NEW-…) when the real account exists. Cells keep their dates; {account.customerCode} is retired as merged.</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Button size="sm" variant="ghost" onClick={() => { onSave({ isVisible: !account.isVisible }); onClose(); }}>{account.isVisible ? 'Hide column' : 'Show column'}</Button>
        <Button size="sm" onClick={() => { onSave({ markReviewed: true }); onClose(); }} title={`Stamp today (${today}) as reviewed`}>Mark reviewed</Button>
        <Button size="sm" variant="primary" disabled={route === 'VIA' && !via} onClick={() => { onSave({ supplyRoute: route || null, supplyViaCustomerCode: route === 'VIA' ? via : null, supplyNote: note || null, ownerName: owner || null, channel: channel || null }); onClose(); }}>Save</Button>
      </div>
    </Popover>
  );
}

/* ── + Add customer ───────────────────────────────────────────────────────── */
function AddCustomerModal({ apiFetch, existing, onClose, onDone }) {
  const [mode, setMode] = useState('existing');
  const [list, setList] = useState(null);
  const [pick, setPick] = useState('');
  const [name, setName] = useState('');
  const [shortName, setShortName] = useState('');
  const [channel, setChannel] = useState('Other');
  const [route, setRoute] = useState('');
  const [via, setVia] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => { apiFetch('/master-customers').then((r) => (r.ok ? r.json() : [])).then((l) => setList(Array.isArray(l) ? l : [])).catch(() => setList([])); }, [apiFetch]);
  const inMatrix = new Set(existing.filter((a) => a.isVisible).map((a) => a.customerCode));
  const candidates = (list ?? []).filter((c) => (c.isActive ?? true) && !inMatrix.has(c.customerCode)).sort((a, b) => (a.shortName ?? a.name).localeCompare(b.shortName ?? b.name));
  async function submit() {
    setBusy(true); setErr(null);
    try {
      const body = mode === 'existing' ? { customerCode: pick } : { newCustomer: { name, shortName: shortName || undefined, channel, supplyRoute: route || undefined, supplyViaCustomerCode: route === 'VIA' ? via : undefined } };
      const res = await apiFetch('/ranging-accounts', { method: 'POST', body: JSON.stringify(body) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(res.status === 403 ? 'Read-only (Viewer) access.' : (j?.error ?? `Failed (${res.status})`));
      onDone(j.created ? `Created ${name} as ${j.customerCode} and added the column.` : 'Column added.');
    } catch (e) { setErr(String(e.message ?? e)); } finally { setBusy(false); }
  }
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={onClose} />
      <div style={{ position: 'relative', width: 'min(480px, 94vw)', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Add a customer column</div>
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={mode === 'existing'} onChange={() => setMode('existing')} />From the customer master</label>
          <label style={{ display: 'flex', gap: 5, alignItems: 'center' }}><input type="radio" checked={mode === 'new'} onChange={() => setMode('new')} />New account</label>
        </div>
        {mode === 'existing' ? (
          <Select value={pick} onChange={(e) => setPick(e.target.value)}>
            <option value="">{list ? `choose… (${candidates.length})` : 'loading…'}</option>
            {candidates.map((c) => <option key={c.customerCode} value={c.customerCode}>{c.shortName ?? c.name} — {c.customerCode}</option>)}
          </Select>
        ) : (
          <>
            <Input placeholder="Name (e.g. Ampol)" value={name} onChange={(e) => setName(e.target.value)} />
            <Input placeholder="Short name for the column header (optional)" value={shortName} onChange={(e) => setShortName(e.target.value)} />
            <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
              {['Other', 'Major Grocery', 'Independent', 'Wholesale', 'Distributor', 'Discount', 'Discount Variety', 'Online', 'Export', 'Warehouse Club', 'Private Label', 'P&C'].map((c) => <option key={c}>{c}</option>)}
            </Select>
            <div style={{ display: 'flex', gap: 6 }}>
              <Select value={route} onChange={(e) => setRoute(e.target.value)} style={{ flex: 1 }}><option value="">Supply method not set</option><option value="DIRECT">Direct</option><option value="VIA">Via another customer</option></Select>
              {route === 'VIA' && <Select value={via} onChange={(e) => setVia(e.target.value)} style={{ flex: 1 }}><option value="">choose…</option>{(list ?? []).filter((c) => c.isActive ?? true).map((c) => <option key={c.customerCode} value={c.customerCode}>{c.shortName ?? c.name}</option>)}</Select>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Creates a NEW-… customer record. If the account already exists under another name, pick it from the master instead — duplicates are refused.</div>
          </>
        )}
        {err && <div style={{ color: 'var(--danger)' }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" variant="primary" disabled={busy || (mode === 'existing' ? !pick : !name.trim() || (route === 'VIA' && !via))} onClick={submit}>{busy ? 'Adding…' : 'Add column'}</Button>
        </div>
      </div>
    </div>
  );
}
